import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Area, Brush, Customized, Line, XAxis } from 'recharts'
import { ChartContainer } from '../../../../../../../../../../common/chart/chartContainer'
import { Legend } from '../../../../../../../../../../common/chart/legend'
import { Checkbox } from '../../../../../../../../../../common/html/checkbox'
import { Element } from '../../../../../../../../../../common/html/element'
import { formatByTheme } from '../../../../../../../../../../helpers/theme'
import { MARGIN_LEFT_CHART_DYNAMIC } from '../../../../../../../../../../tool/dynamicReport/constant'
import { SCORE_TYPE } from '../../../../../../../../../common/header/companyInfo/constant'
import { selectScoreRanges } from '../../../../../../../../../common/header/companyInfo/store/slice'
import StackColorBarYAxis from '../../../../../../../common/chart/StackColorBarYAxis'
import { PERIOD_TYPE } from '../../../../../../../constant'
import { selectTypePeriod } from '../../../../../../../store/slice'
import { pointRangesChartCreditRisk } from '../../store/slice'
import { TYPE_CHART_RISK } from '../constant'
import classes from '../index.module.scss'
import {
  changeListSelectDynamicCredit,
  selectChartTrendOfScore,
} from '../store/slice'

export const TrendOfScoreChart = ({
  isDynamicReport,
  settingDynamic,
  period,
}) => {
  const dispatch = useDispatch()
  const ref = useRef()

  const pointRanges = useSelector(pointRangesChartCreditRisk)
  const data = useSelector(selectChartTrendOfScore)
  const timeFrequencyStore = useSelector(selectTypePeriod)
  const timeFrequency = isDynamicReport ? period : timeFrequencyStore
  const dataScoreRangeCredit = useSelector(
    selectScoreRanges([SCORE_TYPE.CREDIT_RISK]),
  )

  const [isShowRange, setShowRange] = useState(isDynamicReport ? false : true)
  const [listCheckBox, setListCheckBox] = useState(
    isDynamicReport
      ? settingDynamic
      : TYPE_CHART_RISK.map((item) => item.dataKey),
  )

  useEffect(() => {
    const timeOut = setTimeout(() => {
      const el = ref.current?.querySelector('.recharts-brush-slide')
      if (el) {
        el.setAttribute('rx', 6)
        clearTimeout(timeOut)
      }
    }, 300)
    dispatch(changeListSelectDynamicCredit(listCheckBox))
    return () => clearTimeout(timeOut)
  }, [])

  //Tooltip
  const renderCustomContent = ({ data, renderTooltipLabel }) => {
    return (
      <>
        <div>{renderTooltipLabel()}</div>
        <div>
          <table className={classes.table}>
            <tbody>
              <tr>
                <td>{data.description1}</td>
                <td>{data.data1}</td>
              </tr>
              {!!data.dataFloatBar2 &&
                listCheckBox.some((item) => item === 'data2') && (
                  <tr>
                    <td>{data.description2}</td>
                    <td>
                      {data.data2}&nbsp;
                      {isShowRange && <>({data.dataFloatBar2?.join('-')})</>}
                    </td>
                  </tr>
                )}
              {!!data.dataFloatBar3 &&
                listCheckBox.some((item) => item === 'data3') && (
                  <tr>
                    <td>{data.description3}</td>
                    <td>
                      {data.data3}&nbsp;{' '}
                      {isShowRange && <>({data.dataFloatBar3?.join('-')})</>}
                    </td>
                  </tr>
                )}
              {!!data.dataFloatBar4 &&
                listCheckBox.some((item) => item === 'data4') && (
                  <tr>
                    <td>{data.description4}</td>
                    <td>
                      {data.data4}&nbsp;{' '}
                      {isShowRange && <>({data.dataFloatBar4?.join('-')})</>}
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </>
    )
  }

  //Footer
  const legendItems = [
    {
      title: 'score.premium.FOOTER.COMPANY',
      isI18n: true,
      translateProps: {
        description: data.find((item) => !!item?.description1)?.description1,
      },
      color: TYPE_CHART_RISK[0].color,
      dataKey: 'data1',
      iconCustom: true,
      renderCustomIcon: () => {
        return (
          <Element
            style={{
              height: 5,
              width: 16,
              backgroundColor: '#006AA6',
              cursor: 'default',
            }}
          />
        )
      },
      hidden: !data.some((item) => !!item?.description1),
    },
    {
      title: 'score.premium.FOOTER.INDUSTRY',
      isI18n: true,
      translateProps: {
        description: data.find((item) => !!item?.description2)?.description2,
      },
      color: TYPE_CHART_RISK[1].color,
      dataKey: 'data2',
      isCheckbox: true,
      hidden: !data.some((item) => !!item?.description2),
    },
    {
      title: 'score.premium.FOOTER.REGION',
      isI18n: true,
      translateProps: {
        description: data.find((item) => !!item?.description3)?.description3,
      },
      color: TYPE_CHART_RISK[2].color,
      dataKey: 'data3',
      isCheckbox: true,
      hidden: !data.some((item) => !!item?.description3),
    },
    {
      title: 'score.premium.FOOTER.YEAR_OF',
      isI18n: true,
      translateProps: {
        description: data.find((item) => !!item?.description4)?.description4,
      },
      color: TYPE_CHART_RISK[3].color,
      dataKey: 'data4',
      isCheckbox: true,
      hidden: !data.some((item) => !!item?.description4),
    },
  ]

  const actionCheckBox = (list) => {
    dispatch(changeListSelectDynamicCredit(list))
    setListCheckBox(list)
  }

  const renderContent = (dynamic) => {
    const chart = () => (
      <>
        <div>
          <ChartContainer
            data={data}
            width="100%"
            height={304}
            margin={{
              left: isDynamicReport ? MARGIN_LEFT_CHART_DYNAMIC + 15 : 15,
              right: 10,
              bottom: 6,
            }}
            yAxis={[
              {
                yAxisId: 'left',
                dataKeys: TYPE_CHART_RISK.map((item) => item.dataKey),
                ticks: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
                tickMargin: 24,
                tickFormatter: (value) =>
                  pointRanges.includes(value) ? value : '',
              },
            ]}
            tooltip={{
              schema: {
                renderCustomContent: renderCustomContent,
              },
            }}
          >
            <Customized
              component={StackColorBarYAxis}
              yAxisId="left"
              ranges={{
                breakValues: [30, 50, 70, 90],
                data: [...dataScoreRangeCredit].reverse(),
              }}
            />
            <XAxis
              height={60}
              dataKey="ticker"
              angle={timeFrequency !== PERIOD_TYPE.YEARLY ? -30 : ''}
              textAnchor="middle"
              padding={{ left: 14, right: 30 }}
              axisLine={{ stroke: formatByTheme('#D5D5D5') }}
              tickLine={false}
              stroke="#9C9C9C"
              tickMargin={timeFrequency !== PERIOD_TYPE.YEARLY ? 20 : 9}
              interval={0}
            />
            {TYPE_CHART_RISK.filter((key) =>
              listCheckBox.includes(key.dataKey),
            ).map((item) => (
              <Line
                key={item.id}
                yAxisId="left"
                dataKey={item.dataKey}
                stroke={item.color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            ))}
            {isShowRange &&
              TYPE_CHART_RISK.filter((key) =>
                listCheckBox.includes(key.dataKey),
              ).map((item, index) => (
                <Area
                  key={index}
                  yAxisId="left"
                  dataKey={item.dataFloatBar}
                  stroke="none"
                  fill={item.color}
                  activeDot={false}
                  fillOpacity={0.15}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))}
            {timeFrequency !== PERIOD_TYPE.YEARLY && (
              <Brush
                className="customBrush"
                startIndex={data.length - 12}
                endIndex={data.length - 1}
                rx={4}
                height={6}
                stroke={isDynamicReport ? 'transparent' : '#BCBCBC'}
              />
            )}
          </ChartContainer>
          <div style={{ paddingBottom: 8, paddingTop: 4 }}>
            <Element style={{ lineHeight: '16px', color: '#3A414B' }}>
              <Translate value="score.premium.FOOTER.TEXT_NOTI1" />
            </Element>
          </div>
        </div>
      </>
    )
    const legend = () => (
      <Legend
        items={legendItems}
        style={{ flexDirection: 'column' }}
        listCheckBox={listCheckBox}
        setListCheckBox={actionCheckBox}
      />
    )
    if (dynamic) {
      return (
        <div ref={ref} className="dFlex justifyContentBetween">
          <div className="w70">{chart()}</div>
          <div className="w30 pl2">{legend()}</div>
        </div>
      )
    }
    return (
      <div ref={ref}>
        {chart()}
        {legend()}
      </div>
    )
  }

  return (
    <>
      <div className="dFlex justifyContentBetween">
        <Element
          style={{
            color: '#191D25',
            lineHeight: '16px',
            fontWeight: 600,
          }}
        >
          <Translate value="score.premium.TREND_OF_SCORE" />
        </Element>
        <div>
          {isDynamicReport ? (
            <></>
          ) : (
            <Checkbox
              title="score.premium.SHOW_RANGE"
              isI18n
              checked={isShowRange}
              isCheckOnClickText
              style={{
                paddingLeft: 6,
              }}
              onChange={() => {
                setShowRange((prev) => !prev)
              }}
            />
          )}
        </div>
      </div>
      <Element
        style={{
          color: '#9C9C9C',
          lineHeight: '16px',
          padding: '8px 0',
        }}
      >
        <Translate value="score.premium.MEDIAN_SCORE" />
      </Element>
      {renderContent(isDynamicReport)}
    </>
  )
}
